import { useState, useCallback } from 'react'
import {
  authorizeSellerQueueAction,
  updateQueueStatus,
  updateQueuePosition,
  removeFromQueue,
  updateLastCalledAt,
  updateLastFinishedAt,
  createQueueEntry,
  completeSession,
  createNoSaleReason,
  batchUpdateQueuePositions,
  closeSellerAttendance as closeSellerAttendanceRequest,
  returnSellerToQueue as returnSellerToQueueRequest,
  startSellerAttendance as startSellerAttendanceRequest,
} from '../services'

const getMessage = (err: unknown, fallback: string): string => {
  if (typeof err === 'string' && err.trim()) return err
  if (err instanceof Error && err.message) return err.message
  return fallback
}

export const useQueueActions = (pluginspaceId: number) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const changeStatus = useCallback(
    async (queueId: number, status: string) => {
      try {
        setLoading(true)
        setError(null)
        await updateQueueStatus(queueId, status)
        setLoading(false)
        return true
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao atualizar status')
        setLoading(false)
        return false
      }
    },
    []
  )

  const reorderQueue = useCallback(
    async (sellerId: number, newPosition: number) => {
      try {
        setLoading(true)
        setError(null)
        await updateQueuePosition(sellerId, newPosition, pluginspaceId)
        setLoading(false)
        return true
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao reordenar fila')
        setLoading(false)
        return false
      }
    },
    [pluginspaceId]
  )

  const removeSellerFromQueue = useCallback(
    async (sellerId: number) => {
      try {
        setLoading(true)
        setError(null)
        console.log(`[useQueueActions] removeSellerFromQueue: sellerId=${sellerId}`);
        await removeFromQueue(sellerId)
        setLoading(false)
        console.log(`[useQueueActions] ✅ Removal successful`);
        return true
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Erro ao remover da fila';
        console.error(`[useQueueActions] ❌ Removal failed:`, errorMsg);
        setError(errorMsg)
        setLoading(false)
        return false
      }
    },
    []
  )

  const callSeller = useCallback(async (queueId: number) => {
    try {
      setLoading(true)
      await updateLastCalledAt(queueId)
      setLoading(false)
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao chamar vendedor')
      setLoading(false)
      return false
    }
  }, [])

  const finishCall = useCallback(async (queueId: number) => {
    try {
      setLoading(true)
      await updateLastFinishedAt(queueId)
      setLoading(false)
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao finalizar atendimento')
      setLoading(false)
      return false
    }
  }, [])

  const addToQueue = useCallback(
    async (sellerId: number, position?: number) => {
      try {
        setLoading(true)
        setError(null)
        await createQueueEntry(pluginspaceId, sellerId, position)
        setLoading(false)
        return true
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao adicionar à fila')
        setLoading(false)
        return false
      }
    },
    [pluginspaceId]
  )

  const finishSession = useCallback(
    async (sessionId: number, outcome: string, durationSeconds: number) => {
      try {
        setLoading(true)
        setError(null)
        await completeSession(sessionId, outcome, durationSeconds)
        setLoading(false)
        return true
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao finalizar sessão')
        setLoading(false)
        return false
      }
    },
    []
  )

  const closeSellerAttendance = useCallback(
    async (
      sessionId: number,
      sellerId: number,
      credential: string,
      outcome: 'sale_completed' | 'pending_checkout' | 'no_sale' | 'abandoned',
      reasonCode?: number,
      note?: string,
      saleAmount?: number
    ) => {
      try {
        setLoading(true)
        setError(null)
        await closeSellerAttendanceRequest(
          sessionId,
          sellerId,
          credential,
          outcome,
          reasonCode,
          note,
          saleAmount
        )
        setLoading(false)
        return true
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao encerrar atendimento')
        setLoading(false)
        return false
      }
    },
    []
  )

  const registerNoSaleReason = useCallback(
    async (
      sessionId: number,
      reasonCode: number,
      productName?: string,
      sizeWanted?: string,
      colorWanted?: string,
      note?: string
    ) => {
      try {
        setLoading(true)
        setError(null)
        await createNoSaleReason(sessionId, reasonCode, productName, sizeWanted, colorWanted, note)
        setLoading(false)
        return true
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao registrar motivo')
        setLoading(false)
        return false
      }
    },
    []
  )

  const batchReorderQueue = useCallback(
    async (updates: Array<{ sellerId: number; position: number }>, authCredential?: string) => {
      try {
        console.log('[useQueueActions] 🚀 batchReorderQueue START - Updates:', updates.length)
        setLoading(true)
        setError(null)
        console.log('[useQueueActions] 🔄 Calling batchUpdateQueuePositions API...')
        
        // Timeout: SE não responder em 20s, vai dar erro (Axios já tem 15s, este é safeguard)
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => {
            console.error('[useQueueActions] ⏱️ API TIMEOUT após 20s')
            reject(new Error('API timeout após 20 segundos'))
          }, 20000)
        )

        await Promise.race([
          batchUpdateQueuePositions(updates, authCredential),
          timeoutPromise,
        ])

        console.log('[useQueueActions] ✅✅ API call succeeded')
        setLoading(false)
        return true
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err)
        console.error('[useQueueActions] ❌ batchReorderQueue FAILED:', errorMsg)
        setError(errorMsg)
        setLoading(false)
        return false
      }
    },
    []
  )

  const authorizeAction = useCallback(
    async (
      actionType: 'manual_queue_move' | 'return_to_queue',
      credential: string,
      targetSellerId?: number
    ) => {
      try {
        setLoading(true)
        setError(null)
        const result = await authorizeSellerQueueAction(actionType, credential, targetSellerId)
        setLoading(false)
        return result
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Erro ao validar autorização'
        setError(errorMsg)
        setLoading(false)
        return { authorized: false, message: errorMsg }
      }
    },
    []
  )

  const returnSellerToQueue = useCallback(
    async (
      sessionId: number,
      sellerId: number,
      credential: string,
      outcome: 'sale_completed' | 'no_sale',
      reasonCode?: number,
      note?: string,
      saleAmount?: number
    ) => {
      try {
        setLoading(true)
        setError(null)
        await returnSellerToQueueRequest(
          sessionId,
          sellerId,
          credential,
          outcome,
          reasonCode,
          note,
          saleAmount
        )
        setLoading(false)
        return true
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao retornar vendedor para a fila')
        setLoading(false)
        return false
      }
    },
    []
  )

  const startSellerAttendance = useCallback(async (sellerId: number, queuePositionAtStart?: number) => {
    try {
      setLoading(true)
      setError(null)
      await startSellerAttendanceRequest(sellerId, queuePositionAtStart)
      setLoading(false)
      return true
    } catch (err) {
      const errorMsg = getMessage(err, 'Erro ao iniciar atendimento')
      setError(errorMsg)
      setLoading(false)
      throw new Error(errorMsg)
    }
  }, [])

  return {
    loading,
    error,
    changeStatus,
    reorderQueue,
    batchReorderQueue,
    removeSellerFromQueue,
    callSeller,
    finishCall,
    addToQueue,
    finishSession,
    registerNoSaleReason,
    closeSellerAttendance,
    authorizeAction,
    returnSellerToQueue,
    startSellerAttendance,
  }
}
